import React from 'react';
import { Link } from 'react-router-dom';

/**
 * The redesign's building blocks, as components.
 *
 * Every one of these wraps a class name the prototype defines. That indirection
 * is the point: `className="mc"` typed into eleven screens is eleven places to
 * fix when the prototype renames it, and eleven chances for one of them to be
 * missed and quietly render unstyled. `portal-primitives.spec` fails the build
 * if a screen reaches for the raw class instead.
 *
 * These are structure, not decoration. Nothing here invents a style — the CSS
 * is the prototype's, generated and scoped by scripts/build-redesign-css.js.
 */

interface PortalPageProps {
  /**
   * The prototype's screen id, e.g. 's_courses'.
   *
   * Not decoration. The redesign scopes each screen's rules to its own element
   * — they are written `#s_courses .cc`, not `.cc` — so without the id a screen
   * inherits the tokens and none of the layout, and arrives looking like the
   * old design wearing new colours.
   */
  screenId: string;
  children: React.ReactNode;
}

/**
 * The content column every portal screen sits in.
 *
 * `.layout > .main` is where the redesign sets the column width, gutters and
 * vertical rhythm shared across the portal.
 */
export const PortalPage: React.FC<PortalPageProps> = ({ screenId, children }) => (
  <div id={screenId}>
    <div className="layout">
      <main className="main">{children}</main>
    </div>
  </div>
);

interface PageHeaderProps {
  title: string;
  /** The line under the title — a date, a count, a period. */
  subtitle?: React.ReactNode;
  /** Filters or period switches, right-aligned on wide screens. */
  actions?: React.ReactNode;
}

/** The `.welcome` block: title, one line of context, and the screen's controls. */
export const PageHeader: React.FC<PageHeaderProps> = ({ title, subtitle, actions }) => (
  <div className="welcome">
    <div>
      <h1>{title}</h1>
      {subtitle ? <div className="welcome-sub">{subtitle}</div> : null}
    </div>
    {actions}
  </div>
);

/** The `.metrics` grid. Four across on desktop, two on tablet, one on phone. */
export const MetricGrid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="metrics">{children}</div>
);

interface MetricCardProps {
  label: string;
  value: string;
  /** When given, the whole tile is a link. */
  to?: string;
  /** Overrides the value's colour — used for money and for warnings. */
  accent?: string;
}

/**
 * One `.mc` tile.
 *
 * Named for what it says rather than what it shows when it links somewhere: a
 * link whose entire accessible name is "4" tells a screen-reader user nothing.
 */
export const MetricCard: React.FC<MetricCardProps> = ({ label, value, to, accent }) => {
  const body = (
    <>
      <div className="mc-val" style={accent ? { color: accent } : undefined}>
        {value}
      </div>
      <div className="mc-label">{label}</div>
    </>
  );

  return to ? (
    <Link to={to} aria-label={`${label}: ${value}`} className="mc" style={{ display: 'block' }}>
      {body}
    </Link>
  ) : (
    <div className="mc">{body}</div>
  );
};

interface DesignedNotBuiltProps {
  title: string;
  /** One line on what this screen is meant to do, from the prototype. */
  purpose: string;
}

/**
 * A screen the redesign names and neither we nor the prototype can show.
 *
 * The educator and family prototypes draw their unbuilt screens in HTML, so
 * those render the real markup under a banner. The operations prototype does
 * not: its screen containers are empty and filled at runtime from invented
 * figures. Porting that would put numbers on an ops console that no queue
 * produced — and a dashboard whose counts are decorative is worse than one
 * that admits it has none.
 *
 * So this says what the screen is for, and stops.
 */
export const DesignedNotBuilt: React.FC<DesignedNotBuiltProps> = ({ title, purpose }) => (
  <div className="card" style={{ padding: 28, maxWidth: 680, margin: '28px auto' }}>
    <h1 style={{ marginBottom: 6 }}>{title}</h1>
    <p style={{ color: 'var(--text-2)', margin: '0 0 18px', lineHeight: 1.6 }}>{purpose}</p>
    <div
      role="note"
      style={{
        padding: '10px 14px',
        borderRadius: 12,
        background: 'var(--warn-soft, #FFF4DE)',
        border: '1px solid rgba(255,176,32,.35)',
        color: '#7A5200',
        fontSize: 13,
        fontWeight: 700,
      }}
    >
      Diseñado, todavía sin construir. El prototipo de operaciones genera esta pantalla en tiempo de
      ejecución con datos inventados, así que no hay maquetación que mostrar aquí.
    </div>
  </div>
);
